import * as React from "react";
import {
  TextField,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { updateWash } from "../../State/Wash/Action";
import { getEstimatedPrice } from "../../State/Booking/Action";
import moment from "moment";
import MultipleSelect from "../Tools/MultipleSelect";
import { getWorkers } from "../../State/Worker/Action";
import DateTimeDropDown from "../Tools/DateTimeDropDown";
import {
  DEFAULT_WASH_TYPE,
  VEHICLE_WASH_TYPE_OPTIONS,
  WASH_TYPE_LABELS,
  extractSeaterFromWashType,
  extractWashCode,
  formatWashTypeKey,
} from "../../constants/washTypes";

export default function UpdateWashDialog({
  bookingId,
  vehicle,
  bookedAt,
  seater,
  workerIdList,
  washType,
}) {
  const [open, setOpen] = React.useState(false);
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = React.useState("");
  const [washStatus, setWashStatus] = React.useState("COMPLETED");
  const { booking } = useSelector((store) => store);

  const [discount, setDiscount] = React.useState(0);
  const [additionalServiceCharge, setAdditionalServiceCharge] =
    React.useState(0);
  const [taxableAmount, setTaxableAmount] = React.useState(0);
  const [gst, setGst] = React.useState(0);
  const [totalAmount, setTotalAmount] = React.useState(0);
  const { worker } = useSelector((store) => store);
  const [workerIds, setWorkerIds] = React.useState(workerIdList || []);
  const initialSeater =
    seater ||
    extractSeaterFromWashType(washType) ||
    (vehicle === "CAR" ? "FIVE_SEATER" : "TWO_SEATER");
  const [seaterType, setSeater] = React.useState(initialSeater);
  const extractedWashCode = extractWashCode(washType);
  const normalizedWash =
    extractedWashCode &&
    VEHICLE_WASH_TYPE_OPTIONS[vehicle]?.includes(extractedWashCode)
      ? extractedWashCode
      : VEHICLE_WASH_TYPE_OPTIONS[vehicle]?.[0] || DEFAULT_WASH_TYPE;
  const [wash, setWash] = React.useState(normalizedWash);
  const [completedAt, setCompletedAt] = React.useState(
    moment().format("YYYY-MM-DD HH:mm:ss.SSS")
  );
  const [cancelledAt, setCancelledAt] = React.useState(
    moment().format("YYYY-MM-DD HH:mm:ss.SSS")
  );

  const handleClickOpen = () => {
    // Reset wash type to original when opening dialog
    const baseType = extractWashCode(washType);
    if (
      baseType &&
      VEHICLE_WASH_TYPE_OPTIONS[vehicle]?.includes(baseType)
    ) {
      setWash(baseType);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleServiceChargeChange = (e) => {
    const charge = Number(e.target.value);
    setAdditionalServiceCharge(charge);
    recalculateAmounts(charge, discount);
  };

  const handleDiscountChange = (e) => {
    const disc = Number(e.target.value);
    setDiscount(disc);
    recalculateAmounts(additionalServiceCharge, disc);
  };

  const recalculateAmounts = (serviceCharge, disc) => {
    const taxableAmt =
      Number(booking.estimatedCost.basePrice || 0) +
      Number(booking.estimatedCost.travelCharge || 0) +
      Number(serviceCharge) -
      Number(disc);

    const gstAmt = taxableAmt * 0.18;
    const totalAmt = taxableAmt + gstAmt;

    setTaxableAmount(taxableAmt);
    setGst(gstAmt);
    setTotalAmount(totalAmt);
  };

  React.useEffect(() => {
    dispatch(getWorkers());
  }, [dispatch]);

  React.useEffect(() => {
    if (washStatus === "COMPLETED") {
      var day = moment(bookedAt).format("dddd").toUpperCase();
      dispatch(getEstimatedPrice(formatWashTypeKey(wash, vehicle), day, seaterType));
    }
  }, [open, dispatch, vehicle, wash, seaterType, bookedAt, washStatus]);

  React.useEffect(() => {
    if (!VEHICLE_WASH_TYPE_OPTIONS[vehicle]?.includes(wash)) {
      setWash(
        VEHICLE_WASH_TYPE_OPTIONS[vehicle]?.[0] || DEFAULT_WASH_TYPE
      );
    }
  }, [vehicle, wash]);

  React.useEffect(() => {
    if (booking?.estimatedCost) {
      setDiscount(booking.estimatedCost.discount || 0);
      setAdditionalServiceCharge(
        booking.estimatedCost.additionalServiceCharge || 0
      );

      const taxableAmt =
        Number(booking.estimatedCost.basePrice || 0) +
        Number(booking.estimatedCost.travelCharge || 0) +
        Number(booking.estimatedCost.additionalServiceCharge || 0) -
        Number(booking.estimatedCost.discount || 0);

      const gstAmt = taxableAmt * 0.18;
      const totalAmt = taxableAmt + gstAmt;

      setTaxableAmount(taxableAmt);
      setGst(gstAmt);
      setTotalAmount(totalAmt);
    }
  }, [booking.estimatedCost]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (washStatus === "CANCELLED") {
      dispatch(
        updateWash({
          bookingId,
          wash: {
            status: washStatus,
            vehicle,
            vehicleType: vehicle,
            cancelledAt,
          },
          setErrorMessage,
        })
      );
      return;
    }

    const formData = new FormData(event.target);
    const washObj = {
      vehicle,
      vehicleType: vehicle,
      status: washStatus,
      remarks: formData.get("remarks"),
      cancelledAt,
      completedAt,
      additionalServiceCharge,
      discount,
      basePrice: booking.estimatedCost.basePrice,
      travelCharge: booking.estimatedCost.travelCharge,
      taxableAmount,
      gst,
      totalAmount,
      workerIds,
      seater: seaterType,
      washType: wash,
      vehicleNumber: formData.get("vehicleNumber"),
      partnerEarning: formData.get("partnerEarning"),
    };

    if (!validateForm(washObj)) return;

    dispatch(updateWash({ bookingId, wash: washObj, setErrorMessage }));
  };

  const validateForm = (washObj) => {
    if (
      washObj.vehicleNumber.trim() === "" ||
      washObj.partnerEarning.trim() === ""
    ) {
      setErrorMessage("Vehicle Number and Partner Earning are required.");
      return false;
    }

    const partnerEarning = parseFloat(washObj.partnerEarning);
    const total = parseFloat(washObj.totalAmount);

    if (partnerEarning > total) {
      setErrorMessage(
        "Partner Earning cannot be more than 50% of Total Amount."
      );
      return false;
    }

    return true;
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleClickOpen}>
        Update Wash
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogTitle className="text-center font-bold text-green-700">
          <strong>UPDATE WASH</strong>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <form onSubmit={handleSubmit}>
              <div>
                <InputLabel>Booking Id: {bookingId}</InputLabel>
                <div className="mt-2 mb-2">
                  <InputLabel>Wash Status</InputLabel>
                  <Select
                    defaultValue="COMPLETED"
                    name="washStatus"
                    onChange={(e) => setWashStatus(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="COMPLETED">COMPLETE</MenuItem>
                    <MenuItem value="CANCELLED">CANCEL</MenuItem>
                  </Select>
                </div>

                {washStatus === "CANCELLED" && (
                  <div className="mt-2 mb-2">
                    <InputLabel>Vehicle: {vehicle}</InputLabel>
                    <DateTimeDropDown setDateTime={setCancelledAt} delay={-8} />
                  </div>
                )}

                {washStatus === "COMPLETED" && (
                  <>
                    <div className="mt-2 mb-2">
                      <InputLabel id="wash-label">Wash type</InputLabel>
                    <Select
                        labelId="wash-select-label"
                        id="wash-select"
                      onChange={(e) => setWash(e.target.value)}
                      value={wash}
                        name="washType"
                      >
                      {VEHICLE_WASH_TYPE_OPTIONS[vehicle]?.map((type) => (
                        <MenuItem key={type} value={type}>
                          {WASH_TYPE_LABELS[type] || type}
                        </MenuItem>
                      ))}
                      </Select>
                    </div>

                    {vehicle === "CAR" && (
                      <div className="mt-2 mb-2">
                        <InputLabel id="seater-label">Seater</InputLabel>
                        <Select
                          labelId="seater-select-label"
                          id="seater-select"
                          defaultValue={seaterType}
                          onChange={(e) => setSeater(e.target.value)}
                          name="seaterType"
                        >
                          <MenuItem value="FIVE_SEATER">5 Seater</MenuItem>
                          <MenuItem value="SIX_SEVEN_SEATER">
                            6-7 Seater
                          </MenuItem>
                          <MenuItem value="EIGHT_AND_MORE_SEATER">
                            8+ Seater
                          </MenuItem>
                        </Select>
                      </div>
                    )}

                    <MultipleSelect
                      objectMap={worker?.workers}
                      setIds={setWorkerIds}
                      workerIds={workerIdList}
                    />

                    <DateTimeDropDown setDateTime={setCompletedAt} delay={-8} />

                    <TextField
                      label="Partner Earning"
                      type="number"
                      name="partnerEarning"
                      defaultValue={150}
                      fullWidth
                    />

                    <div className="flex justify-between">
                      <span>Base Price:</span>
                      <span>₹{booking.estimatedCost?.basePrice}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Travel Charge:</span>
                      <span>₹{booking.estimatedCost?.travelCharge}</span>
                    </div>
                    <TextField
                      label="Additional Service Charge"
                      fullWidth
                      value={additionalServiceCharge}
                      onChange={handleServiceChargeChange}
                    />
                    <TextField
                      label="Discount in ₹"
                      fullWidth
                      value={discount}
                      onChange={handleDiscountChange}
                    />
                    <div className="flex justify-between">
                      <span>Taxable Amount:</span>
                      <span>₹{taxableAmount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>GST (18%):</span>
                      <span>₹{gst}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total:</span>
                      <span>₹{totalAmount}</span>
                    </div>
                    <TextField
                      label="Vehicle Number"
                      name="vehicleNumber"
                      fullWidth
                    />
                    <TextField
                      label="Remarks"
                      name="remarks"
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </>
                )}
              </div>

              <div className="flex justify-center mt-2">
                <Button variant="contained" type="submit" color="primary">
                  Update
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </div>
              {errorMessage && (
                <span className="text-red-700">{errorMessage}</span>
              )}
            </form>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
}
